import { CompStructure, ContentRole } from '@wix/document-services-types';
import {
  AiMediaData,
  AiMediaItem,
  PresetContentFieldRole,
  createFlatStructureMap,
  getImgObj,
} from '@wix/editor-content-injector';
import _ from 'lodash';
import { ExtendedContentTypes, UploadFileFn } from '../types';
import {
  DEFAULT_IMAGE_ASPECT_RATIO,
  DEFAULT_IMAGE_RESOLUTION,
  IMAGE_ASPECT_RATIO_BY_FIELD_ROLE,
  IMAGE_RESOLUTION_BY_FIELD_ROLE,
  DEFAULT_STRUCTURE_ID,
  ScenePromptIds,
  TEAM_MEMBERS_STRUCTURE_ID,
  WIDE_LANDSCAPE_IMAGE_ASPECT_RATIO,
  WIDE_LANDSCAPE_IMAGE_RESOLUTION,
} from './consts';
import {
  AbstractSectionName,
  DefaultSectionName,
  FIELD_ROLES,
  ImageDescription,
  ImageDetails,
  ImagesMap,
  UploadImagesDetails,
} from './types';
import { getBlob } from './fetchDataUtils';
import { AiImageNSFWBlacklist } from './nsfwBlacklist';

export const getImagesDataFromStructure = (
  presetStructure: CompStructure,
): ImagesMap => {
  const flatCompStructureMap = createFlatStructureMap(presetStructure);
  return getImagesData(flatCompStructureMap);
};

export const getImagesData = (
  flatCompStructureMap: CompStructure[],
): ImagesMap => {
  const imagesMap: ImagesMap = new Map();

  flatCompStructureMap.forEach((compStructure) => {
    const imageObject = getImgObj(compStructure);
    if (!imageObject) {
      return;
    }

    const fieldRole = compStructure.contentRole?.fieldRole || '';
    const isWideLandscape =
      fieldRole === FIELD_ROLES.CROPTYPE_LANDSCAPE &&
      compStructure.layout?.width === 980;
    const aspectRatio = isWideLandscape
      ? WIDE_LANDSCAPE_IMAGE_ASPECT_RATIO
      : IMAGE_ASPECT_RATIO_BY_FIELD_ROLE[fieldRole] ||
        DEFAULT_IMAGE_ASPECT_RATIO;
    const imageResolution = isWideLandscape
      ? WIDE_LANDSCAPE_IMAGE_RESOLUTION
      : IMAGE_RESOLUTION_BY_FIELD_ROLE[fieldRole] || DEFAULT_IMAGE_RESOLUTION;
    const allowedFlows = getAllowedFlows(compStructure.contentRole);
    const isTextOnImageFlow =
      fieldRole === PresetContentFieldRole.TextOnImage && allowedFlows.length;

    if (compStructure.id) {
      imagesMap.set(compStructure.id, {
        ...imageResolution,
        aspectRatio,
        fieldRole,
        ...(isTextOnImageFlow && { allowedFlows }),
      });
    }
  });

  return imagesMap;
};

const getAllowedFlows = (contentRole?: ContentRole): string[] => {
  return contentRole?.type === 'Field' || contentRole?.type === 'ContainerRole'
    ? contentRole.allowedFlows ?? []
    : [];
};

function isDefaultSectionName(value: string): value is DefaultSectionName {
  return Object.values(DefaultSectionName).includes(
    value as DefaultSectionName,
  );
}

function containsAbstract(str: string): boolean {
  const regex = /(ABSTRACT|abstract)/;
  return regex.test(str);
}

function containsTeam(sectionName: string): boolean {
  const regex = /team/i;
  return regex.test(sectionName);
}

export const getScenePromptId = (sectionName: string, groupName: string) => {
  if (containsAbstract(groupName)) {
    return ScenePromptIds[AbstractSectionName.ABSTRACT];
  } else if (containsTeam(sectionName)) {
    return ScenePromptIds[DefaultSectionName.TEAM];
  } else if (
    !isDefaultSectionName(sectionName) ||
    sectionName === (ExtendedContentTypes.Header as string)
  ) {
    return ScenePromptIds[DefaultSectionName.GENERAL];
  } else {
    return ScenePromptIds[sectionName];
  }
};

export const getStructurePromptId = (sectionName: string) => {
  const isTeamMembersSection = containsTeam(sectionName);
  const isTestimonialsSection = sectionName === DefaultSectionName.TESTIMONIALS;
  return isTeamMembersSection || isTestimonialsSection
    ? TEAM_MEMBERS_STRUCTURE_ID
    : DEFAULT_STRUCTURE_ID;
};

export const getImagesDescriptions = (
  imagesMap: ImagesMap,
): Record<string, ImageDescription> => {
  return Array.from(imagesMap.keys()).reduce((acc, compId) => {
    acc[compId] = {};
    return acc;
  }, {});
};

export const getGroupedMapByCropType = (
  imagesMap: ImagesMap,
  sectionName: string,
): Map<string, ImagesMap> => {
  const groupedMap = new Map();

  imagesMap.forEach((item, key) => {
    const { fieldRole } = item;
    const croptype =
      fieldRole === FIELD_ROLES.ABSTRACT
        ? FIELD_ROLES.CROPTYPE_ABSTRACT
        : fieldRole;
    const groupName = `${sectionName}_${croptype}`;

    const items = groupedMap.get(groupName) ?? new Map();
    items.set(key, item);
    groupedMap.set(groupName, items);
  });

  return groupedMap;
};

export const getLastNMapItems = <K, V>(
  map: Map<K, V>,
  n: number,
): Map<K, V> => {
  const lastNEntries = _.takeRight(Array.from(map.entries()), n);
  return new Map(lastNEntries);
};

export const getUploadImagesDataFromStructure = async (
  presetStructure: CompStructure,
  pageName: string,
  sectionTitle: string,
): Promise<UploadImagesDetails[]> => {
  const flatCompStructureMap = createFlatStructureMap(presetStructure);
  const imagesMap: UploadImagesDetails[] = [];

  for (const compStructure of flatCompStructureMap) {
    const compId = compStructure.id;
    const imageObject = getImgObj(compStructure);
    if (!imageObject || !compId) {
      continue;
    }

    try {
      const fileName = `${pageName}-${sectionTitle}-${imagesMap.length + 1}`;
      const wixStaticBaseUrl = 'https://static.wixstatic.com';
      const { height, width, uri } = imageObject;
      imagesMap.push([
        compId,
        {
          height,
          width,
          ...(!uri.startsWith(wixStaticBaseUrl)
            ? { localUri: uri }
            : { staticUri: uri }),
          displayName: fileName,
        },
      ]);
    } catch (error) {
      console.error(
        `Failed to fetch and process image for ${compStructure.id}:`,
        error,
      );
    }
  }

  return imagesMap;
};

export const imageDetailsToAiMediaItem = ({
  localUri,
  staticUri,
  ...data
}: ImageDetails): AiMediaItem => ({
  ...data,
  uri: staticUri ?? (localUri as string),
});

export const createAiMediaData = (
  images: Record<string, ImageDetails>,
): AiMediaData => {
  return Object.keys(images).reduce(
    (acc, key) => ({
      ...acc,
      [key]: imageDetailsToAiMediaItem(images[key]),
    }),
    {},
  );
};

const createFileName = (name: string, type: string) => {
  return `${name}.${type}`;
};

export const getStaticUriFromBlob = async (
  localUri: string,
  displayName: string,
  fileUploadFn: UploadFileFn,
) => {
  const imageType = 'png';
  const blob = await getBlob(localUri);
  if (!blob) {
    throw new Error('Failed to fetch image blob');
  }
  const file = new File([blob], createFileName(displayName, imageType), {
    type: `image/${imageType}`,
  });
  const staticUri = await fileUploadFn(file);
  return staticUri;
};

export const getIsAiImageNSFWBlacklisted = (businessTermId?: string) => {
  if (!businessTermId) return false;

  return AiImageNSFWBlacklist.includes(businessTermId);
};

export function isValidUrlFormat(url: string) {
  try {
    const parsedUrl = new URL(url);
    return !!parsedUrl;
  } catch (e) {
    return false;
  }
}
